import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { 
  MenuSection, 
  MenuItem, 
  MenuSectionFormData,
  MenuItemFormData,
  Restaurant,
  FirebaseError
} from '@/types';
import type {
  MenuSearchState,
  MenuFilterState,
  SortOptions
} from '@/lib/utils/searchUtils';
import {
  createEmptySearchState,
  createEmptyFilterState,
  createDefaultSortOptions,
  searchAndFilter
} from '@/lib/utils/searchUtils';
import * as menuService from '@/lib/firebase/menus';
import { uploadMenuItemImage, deleteMenuItemImage, extractFileNameFromUrl } from '@/lib/firebase/storage';

interface MenuState {
  currentRestaurant: Restaurant | null;
  sections: MenuSection[];
  items: MenuItem[];
  selectedItems: string[];
  loading: boolean;
  error: string | null;
  isMenuPublished: boolean;
  hasExistingMenu: boolean;
  
  // Loading states for specific operations
  reorderingSections: string[]; // IDs of sections being reordered (both affected)
  reorderingItems: string[]; // IDs of items being reordered (both affected)
  
  // Search and Filter State
  searchState: MenuSearchState;
  filterState: MenuFilterState;
  sortOptions: SortOptions;
  isFilterPanelOpen: boolean;
  filteredResults: MenuItem[];
  
  // Actions
  setCurrentRestaurant: (restaurant: Restaurant | null) => void;
  fetchMenu: (restaurantId: string) => Promise<void>;
  
  // Section actions
  addSection: (data: MenuSectionFormData) => Promise<MenuSection>;
  updateSection: (id: string, data: Partial<MenuSectionFormData>) => Promise<void>;
  deleteSection: (id: string) => Promise<void>;
  reorderSections: (sections: MenuSection[]) => Promise<void>;
  
  // Menu item actions
  addItem: (data: MenuItemFormData & { imageFile?: File }) => Promise<MenuItem>;
  updateItem: (id: string, data: Partial<MenuItemFormData> & { imageFile?: File }) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  toggleItemAvailability: (id: string) => Promise<void>;
  reorderItems: (sectionId: string, items: MenuItem[]) => Promise<void>;
  
  // Bulk operations
  setSelectedItems: (ids: string[]) => void;
  toggleItemSelection: (id: string) => void;
  clearSelection: () => void;
  deleteSelectedItems: () => Promise<void>;
  toggleSelectedItemsAvailability: (available: boolean) => Promise<void>;
  
  // Utility actions
  getItemsBySection: (sectionId: string) => MenuItem[];
  getSectionItemCount: (sectionId: string) => number;
  clearError: () => void;
  reset: () => void;

  // Search and Filter Actions
  setSearchQuery: (query: string) => void;
  setFilters: (filters: Partial<MenuFilterState>) => void;
  setSortOptions: (sort: SortOptions) => void;
  toggleFilterPanel: () => void;
  setFilterPanelOpen: (open: boolean) => void;
  clearSearch: () => void;
  clearFilters: () => void;
  updateFilteredResults: () => void;
}

const useMenuStore = create<MenuState>()(
  devtools(
    (set, get) => ({
      currentRestaurant: null,
      sections: [],
      items: [],
      selectedItems: [],
      loading: false,
      error: null,
      isMenuPublished: false,
      hasExistingMenu: false,
      
      // Loading states for specific operations
      reorderingSections: [],
      reorderingItems: [],
      
      // Search and Filter State
      searchState: createEmptySearchState(),
      filterState: createEmptyFilterState(),
      sortOptions: createDefaultSortOptions(),
      isFilterPanelOpen: false,
      filteredResults: [],

      setCurrentRestaurant: (restaurant) => {
        set({ currentRestaurant: restaurant });
      },

      fetchMenu: async (restaurantId: string) => {
        set({ loading: true, error: null });
        
        try {
          const menu = await menuService.getMenu(restaurantId);
          
          // Handle both existing menus and new restaurants (empty menus)
          set({ 
            sections: menu?.sections || [],
            items: menu?.items || [],
            isMenuPublished: menu?.isPublished || false,
            hasExistingMenu: menu !== null && (menu.sections.length > 0 || menu.items.length > 0),
            loading: false 
          });
        } catch (error: unknown) {
          console.error('Error fetching menu:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to load menu',
            loading: false 
          });
        }
      },

      addSection: async (data: MenuSectionFormData) => {
        const { currentRestaurant, sections } = get();
        
        if (!currentRestaurant) {
          throw new Error('No restaurant selected');
        }

        set({ loading: true, error: null });

        try {
          const sectionData = {
            ...data,
            restaurantId: currentRestaurant.id,
            order: sections.length,
            isVisible: true,
            collapsed: false,
          };

          const newSection = await menuService.addSection(sectionData);
          
          set((state) => ({
            sections: [...state.sections, newSection],
            loading: false,
            hasExistingMenu: true
          }));

          return newSection;
        } catch (error: unknown) {
          console.error('Error adding section:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to add section',
            loading: false 
          });
          throw error;
        }
      },

      updateSection: async (id: string, data: Partial<MenuSectionFormData>) => {
        set({ loading: true, error: null });

        try {
          await menuService.updateSection(id, data);
          
          set((state) => ({
            sections: state.sections.map((section) =>
              section.id === id 
                ? { ...section, ...data, updatedAt: new Date() }
                : section
            ),
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error updating section:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to update section',
            loading: false 
          });
          throw error;
        }
      },

      deleteSection: async (id: string) => {
        const { currentRestaurant, items } = get();
        
        if (!currentRestaurant) {
          throw new Error('No restaurant selected');
        }

        set({ loading: true, error: null });

        try {
          // Get all items in this section that have images before deleting the section
          const sectionItems = items.filter(item => item.sectionId === id);
          const itemsWithImages = sectionItems.filter(item => item.imageUrl);
          
          console.log(`Deleting section ${id} with ${sectionItems.length} items, ${itemsWithImages.length} have images`);

          // Delete the section from Firestore (this also deletes associated items via Firebase rules or cascade)
          await menuService.deleteSection(id);
          
          // Clean up images for items that were in this section
          if (itemsWithImages.length > 0) {
            const imageCleanupPromises = itemsWithImages.map(async (item) => {
              try {
                console.log(`Cleaning up image for item: ${item.name}, URL: ${item.imageUrl}`);
                const fileName = extractFileNameFromUrl(item.imageUrl!);
                if (fileName) {
                  await deleteMenuItemImage(currentRestaurant.id, fileName);
                  console.log(`Successfully deleted image: ${fileName}`);
                }
              } catch (imageError) {
                console.warn(`Failed to delete image for item ${item.name}:`, imageError);
                // Don't fail the section deletion if image cleanup fails
              }
            });
            
            await Promise.allSettled(imageCleanupPromises);
          }
          
          set((state) => ({
            sections: state.sections.filter((section) => section.id !== id),
            items: state.items.filter((item) => item.sectionId !== id),
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error deleting section:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to delete section',
            loading: false 
          });
          throw error;
        }
      },

      reorderSections: async (reorderedSections: MenuSection[]) => {
        if (reorderedSections.length === 0) return;
        
        // Find which sections were swapped by comparing with current state
        const { sections: currentSections } = get();
        const affectedSectionIds: string[] = [];
        
        // Compare positions to find affected sections
        for (let i = 0; i < reorderedSections.length; i++) {
          if (currentSections[i]?.id !== reorderedSections[i]?.id) {
            affectedSectionIds.push(reorderedSections[i].id);
            // Also add the section that was displaced
            if (currentSections[i]) {
              affectedSectionIds.push(currentSections[i].id);
            }
          }
        }
        
        // Set loading state and optimistically update the UI
        set({ 
          sections: reorderedSections,
          reorderingSections: affectedSectionIds
        });

        try {
          await menuService.reorderSections(reorderedSections);
          set({ reorderingSections: [] });
        } catch (error: unknown) {
          console.error('Error reordering sections:', error);
          const firebaseError = error as FirebaseError;
          // Revert the optimistic update
          set((state) => ({ 
            sections: state.sections,
            reorderingSections: [],
            error: firebaseError.message || 'Failed to reorder sections'
          }));
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      // Menu Item Actions
      addItem: async (data: MenuItemFormData & { imageFile?: File }) => {
        const { currentRestaurant, items, sections } = get();
        
        if (!currentRestaurant) {
          throw new Error('No restaurant selected');
        }

        set({ loading: true, error: null });

        try {
          const section = sections.find(s => s.id === data.sectionId);
          if (!section) {
            throw new Error('Section not found');
          }

          const sectionItems = items.filter(item => item.sectionId === data.sectionId);
          
          // Extract imageFile from data
          const { imageFile, ...formData } = data;
          
          // Clean the data to avoid undefined values in Firebase
          const itemData = {
            name: formData.name,
            description: formData.description || '',
            price: parseFloat(formData.price),
            dietType: formData.dietType,
            sectionId: formData.sectionId,
            restaurantId: currentRestaurant.id,
            order: sectionItems.length,
            isAvailable: true,
            isPopular: false,
            isNew: true,
            isVegan: formData.isVegan || false,
            isJain: formData.isJain || false,
            // Only include optional fields if they have actual values
            ...(formData.preparationTime && formData.preparationTime.trim() !== '' && {
              preparationTime: parseInt(formData.preparationTime)
            }),
            ...(formData.calories && formData.calories.trim() !== '' && {
              calories: parseInt(formData.calories)
            }),
            ...(formData.ingredients && formData.ingredients.trim() !== '' && {
              ingredients: formData.ingredients.split(',').map(i => i.trim()).filter(Boolean)
            }),
            ...(formData.allergens && formData.allergens.trim() !== '' && {
              allergens: formData.allergens.split(',').map(a => a.trim()).filter(Boolean)
            }),
            ...(formData.spiceLevel && { spiceLevel: formData.spiceLevel }),
            // Enhanced diet type fields
            ...(formData.enhancedAllergens && { enhancedAllergens: formData.enhancedAllergens }),
            ...(formData.enhancedSpiceLevel && { enhancedSpiceLevel: formData.enhancedSpiceLevel }),
            ...(formData.cuisineTags && formData.cuisineTags.length > 0 && { cuisineTags: formData.cuisineTags }),
            ...(formData.customTags && formData.customTags.length > 0 && { customTags: formData.customTags }),
            ...(formData.isSignatureDish && { isSignatureDish: formData.isSignatureDish }),
            ...(formData.isChefSpecial && { isChefSpecial: formData.isChefSpecial }),
          };

          const newItem = await menuService.addItem(itemData);
          
          // If there's an image, upload it and update the item
          if (imageFile) {
            try {
              const uploadResult = await uploadMenuItemImage(
                imageFile,
                currentRestaurant.id,
                newItem.id
              );
              
              // Update the item with the image URL
              const imageUpdateData: Partial<MenuItem> = {
                imageUrl: uploadResult.downloadURL,
              };
              
              await menuService.updateItem(newItem.id, imageUpdateData);
              
              // Update local state with image
              const itemWithImage = { ...newItem, ...imageUpdateData };
              
              set((state) => ({
                items: [...state.items, itemWithImage],
                loading: false,
                hasExistingMenu: true
              }));

              return itemWithImage;
            } catch (imageError) {
              console.error('Error uploading image:', imageError);
              // Still add the item without image if upload fails
              set((state) => ({
                items: [...state.items, newItem],
                loading: false,
                hasExistingMenu: true,
                error: 'Item created but image upload failed. You can edit the item to add an image.'
              }));
              
              return newItem;
            }
          } else {
            // No image to upload
            set((state) => ({
              items: [...state.items, newItem],
              loading: false,
              hasExistingMenu: true
            }));

            return newItem;
          }
        } catch (error: unknown) {
          console.error('Error adding item:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to add item',
            loading: false 
          });
          throw error;
        }
      },

      updateItem: async (id: string, data: Partial<MenuItemFormData> & { imageFile?: File }) => {
        const { currentRestaurant, items } = get();
        
        if (!currentRestaurant) {
          throw new Error('No restaurant selected');
        }

        set({ loading: true, error: null });

        try {
          // Extract imageFile from data
          const { imageFile, ...formData } = data;
          
          // Convert form data to proper types for Firebase
          const cleanedData: Record<string, unknown> = { ...formData };
          
          // Handle price conversion
          if (formData.price) {
            cleanedData.price = parseFloat(formData.price);
          }
          
          // Handle preparationTime conversion  
          if (formData.preparationTime) {
            cleanedData.preparationTime = parseInt(formData.preparationTime);
          }
          
          // Handle calories conversion
          if (formData.calories) {
            cleanedData.calories = parseInt(formData.calories);
          }
          
          // Handle ingredients array conversion
          if (formData.ingredients) {
            cleanedData.ingredients = typeof formData.ingredients === 'string' 
              ? formData.ingredients.split(',').map(i => i.trim()).filter(Boolean)
              : formData.ingredients;
          }
          
          // Handle allergens array conversion
          if (formData.allergens) {
            cleanedData.allergens = typeof formData.allergens === 'string'
              ? formData.allergens.split(',').map(a => a.trim()).filter(Boolean) 
              : formData.allergens;
          }

          // Handle enhanced diet type fields
          if (formData.enhancedAllergens !== undefined) {
            cleanedData.enhancedAllergens = formData.enhancedAllergens;
          }
          
          if (formData.enhancedSpiceLevel !== undefined) {
            cleanedData.enhancedSpiceLevel = formData.enhancedSpiceLevel;
          }
          
          if (formData.cuisineTags !== undefined) {
            cleanedData.cuisineTags = formData.cuisineTags;
          }
          
          if (formData.customTags !== undefined) {
            cleanedData.customTags = formData.customTags;
          }
          
          if (formData.isSignatureDish !== undefined) {
            cleanedData.isSignatureDish = formData.isSignatureDish;
          }
          
          if (formData.isChefSpecial !== undefined) {
            cleanedData.isChefSpecial = formData.isChefSpecial;
          }

          // If there's a new image, upload it
          if (imageFile) {
            try {
              // Get current item to check for existing image
              const currentItem = items.find(item => item.id === id);
              
              const uploadResult = await uploadMenuItemImage(
                imageFile,
                currentRestaurant.id,
                id
              );
              
              cleanedData.imageUrl = uploadResult.downloadURL;
              
              // Delete old image if it exists
              if (currentItem?.imageUrl && currentItem.imageUrl !== uploadResult.downloadURL) {
                try {
                  console.log('Replacing image for item:', currentItem.name);
                  console.log('Old image URL:', currentItem.imageUrl);
                  console.log('New image URL:', uploadResult.downloadURL);
                  const oldFileName = extractFileNameFromUrl(currentItem.imageUrl);
                  console.log('Extracted old filename:', oldFileName);
                  if (oldFileName) {
                    await deleteMenuItemImage(currentRestaurant.id, oldFileName);
                    console.log('Successfully deleted old image:', oldFileName);
                  }
                } catch (deleteError) {
                  console.warn('Failed to delete old image:', deleteError);
                  // Don't fail the update if old image deletion fails
                }
              }
            } catch (imageError) {
              console.error('Error uploading image:', imageError);
              // Continue with update without image
              set((state) => ({
                ...state,
                error: 'Item updated but image upload failed. Please try uploading the image again.',
                loading: false 
              }));
            }
          }

          await menuService.updateItem(id, cleanedData);
          
          set((state) => ({
            items: state.items.map((item) =>
              item.id === id 
                ? { ...item, ...cleanedData, updatedAt: new Date() }
                : item
            ),
            loading: false
          }));
          
          set((state) => ({
            items: state.items.map((item) =>
              item.id === id 
                ? { ...item, ...cleanedData, updatedAt: new Date() }
                : item
            ),
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error updating item:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to update item',
            loading: false 
          });
          throw error;
        }
      },

      deleteItem: async (id: string) => {
        const { currentRestaurant, items } = get();
        
        if (!currentRestaurant) {
          throw new Error('No restaurant selected');
        }

        set({ loading: true, error: null });

        try {
          // Get the item to check for image
          const itemToDelete = items.find(item => item.id === id);
          
          // Delete the item from Firestore first
          await menuService.deleteItem(id);
          
          // If item has an image, delete it from storage
          if (itemToDelete?.imageUrl) {
            try {
              console.log('Attempting to delete image for item:', itemToDelete.name, 'URL:', itemToDelete.imageUrl);
              const fileName = extractFileNameFromUrl(itemToDelete.imageUrl);
              console.log('Extracted filename:', fileName);
              if (fileName) {
                await deleteMenuItemImage(currentRestaurant.id, fileName);
                console.log('Successfully deleted image:', fileName);
              }
            } catch (imageError) {
              console.warn('Failed to delete item image:', imageError);
              // Don't fail the deletion if image cleanup fails
            }
          }
          
          set((state) => ({
            items: state.items.filter((item) => item.id !== id),
            selectedItems: state.selectedItems.filter(itemId => itemId !== id),
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error deleting item:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to delete item',
            loading: false 
          });
          throw error;
        }
      },

      toggleItemAvailability: async (id: string) => {
        const { items } = get();
        const item = items.find(i => i.id === id);
        
        if (!item) {
          throw new Error('Item not found');
        }

        set({ loading: true, error: null });

        try {
          const updatedAvailability = !item.isAvailable;
          await menuService.updateItem(id, { isAvailable: updatedAvailability });
          
          set((state) => ({
            items: state.items.map((item) =>
              item.id === id 
                ? { ...item, isAvailable: updatedAvailability, updatedAt: new Date() }
                : item
            ),
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error toggling item availability:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to update item availability',
            loading: false 
          });
          throw error;
        }
      },

      reorderItems: async (sectionId: string, reorderedItems: MenuItem[]) => {
        if (reorderedItems.length === 0) return;
        
        // Find which items were swapped by comparing with current state
        const { items: currentItems } = get();
        const currentSectionItems = currentItems.filter(item => item.sectionId === sectionId);
        const affectedItemIds: string[] = [];
        
        // Compare positions to find affected items
        for (let i = 0; i < reorderedItems.length; i++) {
          if (currentSectionItems[i]?.id !== reorderedItems[i]?.id) {
            affectedItemIds.push(reorderedItems[i].id);
            // Also add the item that was displaced
            if (currentSectionItems[i]) {
              affectedItemIds.push(currentSectionItems[i].id);
            }
          }
        }
        
        // Set loading state and optimistically update the UI
        set((state) => ({
          items: [
            ...state.items.filter(item => item.sectionId !== sectionId),
            ...reorderedItems
          ],
          reorderingItems: affectedItemIds
        }));

        try {
          await menuService.reorderItems(reorderedItems);
          set({ reorderingItems: [] });
        } catch (error: unknown) {
          console.error('Error reordering items:', error);
          const firebaseError = error as FirebaseError;
          // Revert the optimistic update
          set((state) => ({ 
            items: state.items,
            reorderingItems: [],
            error: firebaseError.message || 'Failed to reorder items'
          }));
          throw error;
        }
      },

      // Bulk Operations
      setSelectedItems: (ids: string[]) => {
        set({ selectedItems: ids });
      },

      toggleItemSelection: (id: string) => {
        set((state) => ({
          selectedItems: state.selectedItems.includes(id)
            ? state.selectedItems.filter(itemId => itemId !== id)
            : [...state.selectedItems, id]
        }));
      },

      clearSelection: () => {
        set({ selectedItems: [] });
      },

      deleteSelectedItems: async () => {
        const { selectedItems } = get();
        
        if (selectedItems.length === 0) return;

        set({ loading: true, error: null });

        try {
          await menuService.deleteItems(selectedItems);
          
          set((state) => ({
            items: state.items.filter((item) => !selectedItems.includes(item.id)),
            selectedItems: [],
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error deleting selected items:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to delete selected items',
            loading: false 
          });
          throw error;
        }
      },

      toggleSelectedItemsAvailability: async (available: boolean) => {
        const { selectedItems } = get();
        
        if (selectedItems.length === 0) return;

        set({ loading: true, error: null });

        try {
          await menuService.updateItemsAvailability(selectedItems, available);
          
          set((state) => ({
            items: state.items.map((item) =>
              selectedItems.includes(item.id)
                ? { ...item, isAvailable: available, updatedAt: new Date() }
                : item
            ),
            selectedItems: [],
            loading: false
          }));
        } catch (error: unknown) {
          console.error('Error updating selected items availability:', error);
          const firebaseError = error as FirebaseError;
          set({ 
            error: firebaseError.message || 'Failed to update items availability',
            loading: false 
          });
          throw error;
        }
      },

      // Utility Functions
      getItemsBySection: (sectionId: string) => {
        const { items } = get();
        return items
          .filter(item => item.sectionId === sectionId)
          .sort((a, b) => a.order - b.order);
      },

      getSectionItemCount: (sectionId: string) => {
        const { items } = get();
        return items.filter(item => item.sectionId === sectionId).length;
      },

      // Search and Filter Actions
      setSearchQuery: (query: string) => {
        set((state) => ({
          searchState: { ...state.searchState, query, isSearching: query.length > 0 }
        }));
        get().updateFilteredResults();
      },

      setFilters: (filters: Partial<MenuFilterState>) => {
        set((state) => ({
          filterState: { ...state.filterState, ...filters }
        }));
        get().updateFilteredResults();
      },

      setSortOptions: (sort: SortOptions) => {
        set({ sortOptions: sort });
        get().updateFilteredResults();
      },

      toggleFilterPanel: () => {
        set((state) => ({ isFilterPanelOpen: !state.isFilterPanelOpen }));
      },

      setFilterPanelOpen: (open: boolean) => {
        set({ isFilterPanelOpen: open });
      },

      clearSearch: () => {
        set((state) => ({
          searchState: { ...state.searchState, query: '', isSearching: false }
        }));
        get().updateFilteredResults();
      },

      clearFilters: () => {
        set({ filterState: createEmptyFilterState() });
        get().updateFilteredResults();
      },

      updateFilteredResults: () => {
        const { items, sections, searchState, filterState, sortOptions } = get();
        
        const result = searchAndFilter(items, sections, {
          searchQuery: searchState.query,
          filters: filterState,
          sort: sortOptions
        });
        
        set((state) => ({
          filteredResults: result.items,
          searchState: {
            ...state.searchState,
            results: result.items,
            resultCount: result.count,
            isSearching: false
          }
        }));
      },

      reset: () => {
        set({
          currentRestaurant: null,
          sections: [],
          items: [],
          selectedItems: [],
          loading: false,
          error: null,
          isMenuPublished: false,
          hasExistingMenu: false,
          reorderingSections: [],
          reorderingItems: [],
          searchState: createEmptySearchState(),
          filterState: createEmptyFilterState(),
          sortOptions: createDefaultSortOptions(),
          isFilterPanelOpen: false,
          filteredResults: [],
        });
      },
    }),
    {
      name: 'menu-store',
    }
  )
);

export default useMenuStore;